import { useLoaderData } from 'react-router';

import { RoccoLink } from '@rocco/components/link';
import type { loader } from '@modules/professional/page/loaders/overview-page-loader.server';
import { useItemAdminData } from '~/modules/directory/hook/use-item-admin-data';
import {
    detailCta,
    DetailTileCard,
    type DetailTile,
} from '~/ui-rocco/pages/detail-shared';
import { SectionLabel } from '~/ui-rocco/pages/home-page/components/SectionLabel';
import { getSelectedShowcase } from '../utils/featured-work';
import { formatSectionNumber } from '../utils/section-numbering';

/**
 * §5 — (01) Selected work. The professional's featured projects as a 3-column
 * grid (per the Figma, not a carousel) with a View-all-Projects link. Tiles
 * reuse the shared {@link DetailTileCard} (Save overlay + per-tile BM admin
 * menu); the landscape media aspect overrides the card's default portrait.
 * Product suppliers (e.g. Resene) have no projects of their own, so the grid
 * showcases their featured products and relabels accordingly.
 */
interface ProfessionalSelectedWorkSectionProps {
    sectionNumber?: number;
}

export const ProfessionalSelectedWorkSection = ({
    sectionNumber,
}: ProfessionalSelectedWorkSectionProps = {}) => {
    const { featuredProjects, featuredProducts, professionalOverview } =
        useLoaderData<typeof loader>();

    const { usingProducts, items } = getSelectedShowcase(
        featuredProjects.featuredProjectsList,
        featuredProducts.featuredProductsList,
    );
    const work = items.slice(0, 6);

    const adminDataMap = useItemAdminData(work.map((item) => item.id));

    if (work.length === 0) return null;

    const tiles: DetailTile[] = work.map((item) => ({
        id: item.id,
        link: item.link,
        title: item.title,
        images: item.images,
        subtitle: item.professional?.name,
        professional: {
            id: item.professional?.id,
            name: item.professional?.name,
        },
        pinType: usingProducts ? 'ProductPin' : 'ProjectPin',
        adminData: adminDataMap[item.id],
        professionalLink: professionalOverview.Link,
    }));

    return (
        <section className="w-full bg-[#181818] text-white max-md:px-6 max-md:py-12 md:px-[2.3124rem] md:pt-[7.6rem] md:pb-[6.25rem]">
            <div className="mx-auto w-full max-w-[115.3752rem]">
                {/* Desktop keeps the eyebrow-only divider; mobile mirrors the other
                numbered sections (project page) with a 14px eyebrow + a 32px
                heading and no divider rule. */}
                <div className="md:border-t md:border-white/20 md:pt-[1.062rem]">
                    <SectionLabel className="text-white max-md:text-[0.875rem] md:text-[2rem]">
                        {usingProducts
                            ? `(${formatSectionNumber(sectionNumber ?? 1)} — Selected products)`
                            : `(${formatSectionNumber(sectionNumber ?? 1)} — Selected work)`}
                    </SectionLabel>
                    <h2 className="m-0 font-sans font-normal text-white max-md:mt-4 max-md:text-[2rem] max-md:leading-[1.08] max-md:tracking-[-0.02em] md:hidden">
                        {usingProducts
                            ? `Featured Products by ${professionalOverview.Title}`
                            : `Featured Projects by ${professionalOverview.Title}`}
                    </h2>
                </div>

                <div className="grid grid-cols-1 max-md:gap-6 max-md:pt-8 md:grid-cols-3 md:gap-x-[1.32rem] md:gap-y-[6.25rem] md:pt-[3rem]">
                    {tiles.map((tile) => (
                        <DetailTileCard
                            key={tile.id}
                            tile={tile}
                            theme="dark"
                            mediaClassName="bg-neutral-800 max-md:aspect-[592/430] md:aspect-[592/430]"
                        />
                    ))}
                </div>

                {/* View all — centred below the grid (Figma §5). */}
                <div className="flex justify-center max-md:pt-8 md:pt-[6.25rem]">
                    <RoccoLink
                        to={`${professionalOverview.Link}/${
                            usingProducts ? 'our-products' : 'case-studies'
                        }`}
                        className={detailCta('grey-dark')}
                    >
                        {usingProducts
                            ? 'View all Products'
                            : 'View all Projects'}
                    </RoccoLink>
                </div>
            </div>
        </section>
    );
};
